import type { FC, FormEvent, ReactNode } from 'react'

import { useState } from 'react'
import { Check } from 'lucide-react'

import { Button } from '@/components/generals/button'

type Status = 'idle' | 'loading' | 'success' | 'error'

export const NewsletterForm: FC = (): ReactNode => {
    const [email, setEmail] = useState('')
    const [status, setStatus] = useState<Status>('idle')
    const [message, setMessage] = useState('')

    const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()
        setStatus('loading')
        try {
            const res = await fetch('/api/subscribe', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email })
            })
            const data = (await res.json()) as { ok?: boolean; error?: string }
            if (!res.ok || !data.ok) {
                throw new Error(data.error ?? 'Subscription failed.')
            }
            setStatus('success')
            setMessage('You are on the list. Look out for our next dispatch.')
            setEmail('')
        } catch (err) {
            setStatus('error')
            setMessage(err instanceof Error ? err.message : 'Something went wrong.')
        }
    }

    if (status === 'success') {
        return (
            <div className='inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 px-5 py-3 text-sm font-medium text-primary'>
                <Check className='size-4 shrink-0' />
                {message}
            </div>
        )
    }

    return (
        <div className='w-full max-w-md'>
            <form onSubmit={handleSubmit} className='flex flex-col gap-3 sm:flex-row'>
                <label htmlFor='newsletter-email' className='sr-only'>
                    Email address
                </label>
                <input
                    id='newsletter-email'
                    type='email'
                    required
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder='you@example.com'
                    className='h-11 flex-1 rounded-full border border-border bg-background px-5 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-ring'
                />
                <Button type='submit' disabled={status === 'loading'}>
                    {status === 'loading' ? 'Joining…' : 'Subscribe'}
                </Button>
            </form>

            {status === 'error' && (
                <p className='mt-2 text-sm text-destructive'>
                    {message}
                </p>
            )}
        </div>
    )
}
